import React, { useState } from "react";

import Select from "../../../components/Select";
import { GatewayConfirmDialog, GatewayModal } from "../../../components/GatewayModal";
import { GatewayStatusPill } from "../../../components/GatewayStatus";
import { useNotify } from "../../../ToastContext";
import chevronIcon from "../../../assets/chevron-right.svg";
import {
  AUTO_DETECT_HARDWARE_PROFILE,
  EDGE_CLOUD_TRANSPORT_OPTIONS,
  EDGE_RUNTIME_HARDWARE_PROFILES,
  edgeHardwareProfileFromCatalog,
  fieldbusProtocolLabel,
  formatRuntimeMetricBytes,
  formatRuntimeMetricPercent,
  formatRuntimeTemperature,
  formatRuntimeScanDuration,
  formatRuntimeUptime,
  formatLastSeen,
  installerCommand,
  isRutOSHardwareProfile,
  hardwareProfileOptions,
  normalizeRuntime,
  openRuntimeAI,
  plcDeploymentSecurityView,
  runtimeReenrollmentSettings,
  runtimeInventoryCounts,
  runtimeDevicePerformance,
  runtimeIOPresentation,
  runtimePLCPerformance,
  runtimeSyncClassOptions,
  runtimeUpdateState,
  runtimeVersionLabel,
} from "./platformEdgeRuntimes.model";
// FR202/hardware/programs modals live in a sibling module; import the ones this
// file renders and re-export the ones external consumers import from here.
import { HardwareIOPage, FR202HardwarePage, FR202RetirementMaintenance, FR202_PROFILE_ID } from "./platformEdgeRuntimes.hardware";

// Preserve the original module's public surface: everything moved to the
// hardware/programs siblings is re-exported here so importers (and tests) keep
// importing from "./platformEdgeRuntimes.components".
export { HardwareIOPage, FR202HardwarePage, FR202RetirementMaintenance };
export {
  FR202RetirementConfirmModal,
  fr202RetirementConfirmation,
  fr202RetirementConfirmationMatches,
  pollEdgeCommandUntilTerminal,
} from "./platformEdgeRuntimes.hardware";
export { PLCProgramsPage } from "./platformEdgeRuntimes.programs";


function CopyButton({ value, label = "Copy", className = "gwGhostBtn", onCopied }) {
  const notify = useNotify();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopied?.();
      const copiedItem = String(label || "Value").replace(/^Copy\s+/i, "");
      notify({ type: "success", title: "Copied", message: `${copiedItem} copied to the clipboard.` });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      notify({ type: "error", title: "Copy failed", message: "Select the text and copy it manually." });
    }
  };
  return (
    <button type="button" className={className} onClick={onCopy}>
      {copied ? "Copied" : label}
    </button>
  );
}

/* One-time setup modal. Deliberately has NO backdrop-click close: the secret
   inside cannot be retrieved again, so dismissal must be the explicit
   "I stored it" button. */
export function SetupModal({ enrollment, hardwareProfiles, online, onClose }) {
  const [secretSaved, setSecretSaved] = useState(false);
  const [commandSaved, setCommandSaved] = useState(false);
  const command = installerCommand(enrollment);
  const setupSecret = String(enrollment.bootstrapSecret || "");
  const name = enrollment.gateway?.name || "the device";
  const hardwareProfile = edgeHardwareProfileFromCatalog(enrollment.hardwareProfile, hardwareProfiles);
  const cloudTransport = enrollment.cloudTransport === "mqtt" ? "MQTT" : "WebSocket";
  return (
    <div className="gwOverlay" style={{ zIndex: 110 }}>
      <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 660 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">Connect {name}</div>
            <div className="gwModalSubtitle">
              {isRutOSHardwareProfile(enrollment.hardwareProfile)
                ? "Run one command as root in an SSH session to the router, then paste the secret when it asks."
                : "Run one command on the device, then paste the secret when it asks."}
            </div>
          </div>
        </div>
        <div className="gwModalBody" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="gwEdgeSetupStep">
            <div className="gwFieldLabel">1. Run this command on the device</div>
            <pre className="gwEdgeSetupCode">{command}</pre>
            <div>
              <CopyButton value={command} label="Copy command" className="gwGhostBtn" onCopied={() => setCommandSaved(true)} />
            </div>
          </div>
          <div className="gwEdgeSetupStep">
            <div className="gwFieldLabel">2. Paste this secret when it asks</div>
            <pre className="gwEdgeSetupCode">{setupSecret}</pre>
            <div>
              <CopyButton value={setupSecret} label="Copy secret" className="gwGhostBtn" onCopied={() => setSecretSaved(true)} />
            </div>
            <div className="gwHint">Shown once. Expires in 30 minutes. The pasted secret is not displayed on the device.</div>
          </div>
          <div className="gwEdgeSetupStep">
            <div className="gwFieldLabel">3. {online ? "Runtime online" : "Wait for the device to connect"}</div>
            <div className="gwHint">
              {online
                ? "Setup is complete. This runtime is connected."
                : "Setup installs everything it needs and finishes on its own. This step completes automatically."}
            </div>
          </div>
          {hardwareProfile ? (
            <div className="gwHint">{hardwareProfile.label} · {hardwareProfile.operatingSystem}</div>
          ) : null}
          <div className="gwHint">Cloud transport · {cloudTransport}</div>
        </div>
        <div className="gwModalFooter" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="gwPrimaryBtn" onClick={onClose} disabled={!online && (!secretSaved || !commandSaved)}>
            {online ? "Finish setup" : secretSaved && commandSaved ? "Close setup" : "Copy the command and secret first"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Compatibility export for the runtime page. Confirmation chrome and behavior
// are owned by the shared Gateway UI layer.
export const ConfirmModal = GatewayConfirmDialog;

const EDGE_RETENTION_OPTIONS = Object.freeze([
  { value: "21600", label: "6 hours" },
  { value: "86400", label: "24 hours" },
  { value: "259200", label: "3 days" },
  { value: "604800", label: "7 days" },
]);
const EDGE_SNAPSHOT_OPTIONS = Object.freeze([
  { value: "5", label: "5 seconds" },
  { value: "10", label: "10 seconds" },
  { value: "30", label: "30 seconds" },
  { value: "60", label: "1 minute" },
]);
const EDGE_EVENT_CAPACITY_OPTIONS = Object.freeze([
  { value: "1000", label: "1,000 events" },
  { value: "10000", label: "10,000 events" },
  { value: "50000", label: "50,000 events" },
]);
const EDGE_BYTE_CAPACITY_OPTIONS = Object.freeze([
  { value: "67108864", label: "64 MiB" },
  { value: "268435456", label: "256 MiB" },
  { value: "536870912", label: "512 MiB" },
]);

const optionsWithCurrent = (options, current, suffix) => {
  const value = String(current);
  if (options.some((option) => option.value === value)) return options;
  return [{ value, label: `${Number(current).toLocaleString()} ${suffix}` }, ...options];
};

// Reviewed setup recovery intentionally ends at SetupModal: confirming this
// form rotates the only usable bootstrap credential. The parent owns the API
// mutation and swaps this modal for the one-time setup command only after success.
export function EnrollmentSettingsModal({ gateway, hardwareProfiles, availableCloudTransports = ["websocket"], busy = false, onClose, onConfirm }) {
  const initial = runtimeReenrollmentSettings(gateway);
  const [hardwareProfile, setHardwareProfile] = useState(initial.hardwareProfile);
  const [syncPolicy, setSyncPolicy] = useState(initial.syncPolicy);
  const [cloudTransport, setCloudTransport] = useState(initial.cloudTransport);
  const activeFR202 = gateway?.hardware_profile === FR202_PROFILE_ID;
  const profileCatalog = Array.isArray(hardwareProfiles) && hardwareProfiles.length > 0
    ? hardwareProfiles
    : EDGE_RUNTIME_HARDWARE_PROFILES;
  const catalogOptions = hardwareProfileOptions(profileCatalog)
    .filter((option) => option.value !== AUTO_DETECT_HARDWARE_PROFILE);
  const profileOptions = activeFR202
    ? catalogOptions.filter((option) => option.value === FR202_PROFILE_ID)
    : catalogOptions;
  const selectedProfile = edgeHardwareProfileFromCatalog(hardwareProfile, profileCatalog);
  // A newer control plane can retain a profile that this temporarily older
  // browser has not catalogued. Keep the existing reviewed value valid without
  // exposing its internal ID; changing to another profile still requires an
  // explicit catalog option.
  const preservedProfile = !selectedProfile && hardwareProfile === initial.hardwareProfile && hardwareProfile
    ? { value: hardwareProfile, label: "Current hardware profile", product: "Configured hardware", ioSummary: "" }
    : null;
  const reviewedProfile = selectedProfile || preservedProfile;
  const reviewedProfileOptions = preservedProfile && !profileOptions.some((option) => option.value === preservedProfile.value)
    ? [preservedProfile, ...profileOptions]
    : profileOptions;
  const hasSyncClass = syncPolicy.includeGPIO
    || syncPolicy.includePLC
    || syncPolicy.includeConnectors
    || syncPolicy.includeOutputEvidence;
  const hasSettingsRevision = Number.isSafeInteger(gateway?.enrollment_settings_revision)
    && gateway.enrollment_settings_revision >= 0;
  const valid = Boolean(reviewedProfile && hasSyncClass && hasSettingsRevision);
  const transportOptions = EDGE_CLOUD_TRANSPORT_OPTIONS.filter((option) => (
    availableCloudTransports.includes(option.value) || option.value === initial.cloudTransport
  ));
  const reviewRequired = gateway?.enrollment_settings_review_required === true;
  const updatePolicy = (field, value) => setSyncPolicy((current) => ({ ...current, [field]: value }));

  return (
    <GatewayModal
      title={reviewRequired ? "Review runtime setup" : "Review setup settings"}
      subtitle="Confirm the hardware and bounded cloud synchronization policy."
      onClose={busy ? undefined : onClose}
      width={620}
      bodyClassName="gwFormStack plcRuntimeEnrollmentReview"
      footer={(
        <>
          <button type="button" className="gwGhostBtn" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="gwPrimaryBtn"
            disabled={!valid || busy}
            onClick={() => onConfirm({ hardwareProfile, syncPolicy, cloudTransport })}
          >
            {busy ? "Saving…" : "Save and get setup command"}
          </button>
        </>
      )}
    >
      {reviewRequired ? (
        <div className="gwNotice isWarning">
          <strong>Setup review required</strong>
          <span>Choose the settings for this runtime before its next setup.</span>
        </div>
      ) : null}

      <div className="gwField">
        <label className="gwLabel">Hardware profile</label>
        <Select
          value={hardwareProfile}
          onChange={setHardwareProfile}
          options={reviewedProfileOptions}
          placeholder="Choose hardware profile"
          disabled={busy}
        />
        {reviewedProfile ? (
          <div className="gwHint">{[reviewedProfile.product, reviewedProfile.ioSummary].filter(Boolean).join(" · ")}</div>
        ) : (
          <div className="gwHint">Select the hardware attached to this runtime.</div>
        )}
        {activeFR202 ? (
          <div className="gwHint">Use FR202 retirement to remove this hardware profile.</div>
        ) : null}
      </div>

      <div className="gwField">
        <label className="gwLabel">Cloud transport</label>
        <Select
          value={cloudTransport}
          onChange={setCloudTransport}
          options={transportOptions}
          disabled={busy}
        />
        <div className="gwHint">
          {cloudTransport === "mqtt"
            ? "MQTT relay traffic is encrypted per runtime and is separate from plant MQTT connections."
            : "Standard outbound WebSocket connection."}
        </div>
      </div>

      <div className="gwField">
        <label className="gwLabel">Cloud synchronization</label>
        <div className="gwCheckboxGrid">
          {runtimeSyncClassOptions(selectedProfile).map(([field, label]) => (
            <label className="gwCheckboxRow" key={field}>
              <input
                type="checkbox"
                checked={Boolean(syncPolicy[field])}
                disabled={busy}
                onChange={(event) => updatePolicy(field, event.target.checked)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        {!hasSyncClass ? <div className="gwHint isWarning">Select at least one data class.</div> : null}
      </div>

      <div className="gwFieldRow">
        <div className="gwField">
          <label className="gwLabel">Offline retention</label>
          <Select
            value={String(syncPolicy.retentionSeconds)}
            onChange={(value) => updatePolicy("retentionSeconds", Number(value))}
            options={optionsWithCurrent(EDGE_RETENTION_OPTIONS, syncPolicy.retentionSeconds, "seconds")}
            disabled={busy}
          />
        </div>
        <div className="gwField">
          <label className="gwLabel">Snapshot interval</label>
          <Select
            value={String(syncPolicy.snapshotSeconds)}
            onChange={(value) => updatePolicy("snapshotSeconds", Number(value))}
            options={optionsWithCurrent(EDGE_SNAPSHOT_OPTIONS, syncPolicy.snapshotSeconds, "seconds")}
            disabled={busy}
          />
        </div>
      </div>

      <div className="gwFieldRow">
        <div className="gwField">
          <label className="gwLabel">Event capacity</label>
          <Select
            value={String(syncPolicy.eventCapacity)}
            onChange={(value) => updatePolicy("eventCapacity", Number(value))}
            options={optionsWithCurrent(EDGE_EVENT_CAPACITY_OPTIONS, syncPolicy.eventCapacity, "events")}
            disabled={busy}
          />
        </div>
        <div className="gwField">
          <label className="gwLabel">Disk capacity</label>
          <Select
            value={String(syncPolicy.byteCapacity)}
            onChange={(value) => updatePolicy("byteCapacity", Number(value))}
            options={optionsWithCurrent(EDGE_BYTE_CAPACITY_OPTIONS, syncPolicy.byteCapacity, "bytes")}
            disabled={busy}
          />
        </div>
      </div>
      {!hasSettingsRevision ? (
        <div className="gwNotice isWarning"><span>Reload this runtime before saving settings.</span></div>
      ) : null}
    </GatewayModal>
  );
}

export function RuntimeRollbackModal({ gateway, busy = false, onClose, onConfirm }) {
  const targets = Array.isArray(gateway?.runtime_update?.rollback_targets)
    ? gateway.runtime_update.rollback_targets
    : [];
  const [targetIndex, setTargetIndex] = useState(targets.length > 0 ? "0" : "");
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const target = targets[Number(targetIndex)] || null;
  const expectedCurrentReleaseID = String(gateway?.runtime_update?.rollback_current_release_id || "");
  const expectedCurrentManifestSHA256 = String(gateway?.runtime_update?.rollback_current_manifest_sha256 || "");
  const trimmedReason = reason.trim();
  const valid = !!target
    && !!expectedCurrentReleaseID
    && !!expectedCurrentManifestSHA256
    && confirmation === gateway.name
    && trimmedReason.length >= 3
    && trimmedReason.length <= 512;
  const options = targets.map((item, index) => ({
    value: String(index),
    label: `Version ${item.version || item.release_number}`,
  }));
  return (
    <GatewayModal
      title="Roll back runtime"
      subtitle="Reinstall an earlier version of the runtime software on this device."
      onClose={busy ? undefined : onClose}
      width={560}
      bodyClassName="gwFormStack"
      footer={(
        <>
          <button type="button" className="gwGhostBtn" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="gwDangerBtn"
            data-gw-action="confirm-runtime-rollback"
            disabled={!valid || busy}
            onClick={() => onConfirm({
              schema: "controlseat.edge.runtime-rollback-request",
              version: 1,
              gateway_id: gateway.id,
              gateway_name: gateway.name,
              target_release_id: target.release_id,
              target_manifest_sha256: target.manifest_sha256,
              expected_current_release_id: expectedCurrentReleaseID,
              expected_current_manifest_sha256: expectedCurrentManifestSHA256,
              reason: trimmedReason,
            })}
          >
            {busy ? "Queuing…" : "Roll back runtime"}
          </button>
        </>
      )}
    >
      <div className="gwNotice isWarning">
        <span>Stop all PLC applications first. The device checks the release is genuine before installing anything.</span>
      </div>
      <div className="gwField">
        <label className="gwLabel">Version to restore</label>
        <Select value={targetIndex} onChange={setTargetIndex} options={options} disabled={busy || options.length < 2} />
        {target ? (
          <div className="gwHint gwRuntimeRollbackIdentity" data-gw-runtime-rollback-identity>
            <span>
              The device confirms its current version before installing the one you selected.
            </span>
          </div>
        ) : null}
      </div>
      <div className="gwField">
        <label className="gwLabel" htmlFor="runtime-rollback-reason">Reason</label>
        <textarea
          id="runtime-rollback-reason"
          className="gwInput"
          rows="3"
          maxLength="512"
          value={reason}
          disabled={busy}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why you are going back to this version"
        />
      </div>
      <div className="gwField">
        <label className="gwLabel" htmlFor="runtime-rollback-confirmation">Type {gateway.name} to confirm</label>
        <input
          id="runtime-rollback-confirmation"
          className="gwInput"
          value={confirmation}
          disabled={busy}
          autoComplete="off"
          onChange={(event) => setConfirmation(event.target.value)}
        />
      </div>
    </GatewayModal>
  );
}

export function RuntimeTrustUpdateModal({ gateway, busy = false, onClose, onConfirm }) {
  const trust = gateway?.trust_update || {};
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const trimmedReason = reason.trim();
  const valid = trust.update_available === true
    && !trust.error
    && confirmation === gateway.name
    && trimmedReason.length >= 3
    && trimmedReason.length <= 512;
  return (
    <GatewayModal
      title="Update device security"
      subtitle="Install the next approved security update."
      onClose={busy ? undefined : onClose}
      width={620}
      bodyClassName="gwFormStack"
      footer={(
        <>
          <button type="button" className="gwGhostBtn" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="gwDangerBtn"
            data-gw-action="confirm-runtime-trust-update"
            disabled={!valid || busy}
            onClick={() => onConfirm({
              schema: "controlseat.edge.runtime-trust-update-request",
              version: 1,
              gateway_id: gateway.id,
              gateway_name: gateway.name,
              transition_id: trust.transition_id,
              transition_sha256: trust.transition_sha256,
              bundle_sha256: trust.bundle_sha256,
              expected_current_generation: trust.current_generation,
              expected_current_trust_sha256: trust.current_trust_sha256,
              expected_previous_generation: trust.previous_generation || 0,
              expected_previous_trust_sha256: trust.previous_trust_sha256 || "",
              expected_last_transition_id: trust.last_transition_id || "",
              expected_last_transition_sha256: trust.last_transition_sha256 || "",
              target_generation: trust.target_generation,
              target_trust_sha256: trust.target_trust_sha256,
              expires_at: trust.expires_at,
              reason: trimmedReason,
            })}
          >
            {busy ? "Queuing…" : "Update device security"}
          </button>
        </>
      )}
    >
      <div className="gwNotice isWarning">
        <span>The device verifies this update before applying it and keeps its current settings if verification fails.</span>
      </div>
      <div className="gwField">
        <div className="gwLabel">Security update</div>
        <div className="gwHint gwRuntimeRollbackIdentity" data-gw-runtime-trust-identity>
          <span>Ready to install on {gateway.name}.</span>
        </div>
      </div>
      <div className="gwField">
        <label className="gwLabel" htmlFor="runtime-trust-update-reason">Reason</label>
        <textarea
          id="runtime-trust-update-reason"
          className="gwInput"
          rows="3"
          maxLength="512"
          value={reason}
          disabled={busy}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why device security is being updated"
        />
      </div>
      <div className="gwField">
        <label className="gwLabel" htmlFor="runtime-trust-update-confirmation">Type {gateway.name} to confirm</label>
        <input
          id="runtime-trust-update-confirmation"
          className="gwInput"
          value={confirmation}
          disabled={busy}
          autoComplete="off"
          onChange={(event) => setConfirmation(event.target.value)}
        />
      </div>
    </GatewayModal>
  );
}

export function RuntimeDetailView({ gateway, hardwareProfiles, canConfigure, canWrite, busyID, onBack, onApplications, onConnections, onHardwareSetup, onChanged, onUpdateRuntime, onRollbackRuntime, onUpdateTrust, onReviewSettings, onRotate, onRemove }) {
  const runtime = normalizeRuntime(gateway.runtime);
  const counts = runtimeInventoryCounts(gateway);
  const devicePerformance = runtimeDevicePerformance(runtime);
  const plcPerformance = runtimePLCPerformance(runtime);
  const update = gateway.runtime_update || null;
  const updateState = runtimeUpdateState(gateway);
  const updateInProgress = updateState.mode === "updating";
  const updateAvailable = updateState.mode === "self";
  const manualUpdate = updateState.mode === "manual";
  const updatePaused = updateState.mode === "paused";
  const fieldbusProtocols = Array.isArray(runtime?.fieldbus?.protocols) ? runtime.fieldbus.protocols : [];
  const availableFieldbusProtocols = fieldbusProtocols.filter((protocol) => (
    protocol?.readiness === "ready"
    && protocol?.execution_available === true
    && fieldbusProtocolLabel(protocol.protocol)
  ));
  const deploymentSecurity = plcDeploymentSecurityView(gateway);
  const canMaintainRuntime = !!canConfigure && !!canWrite;
  const rollbackTargets = Array.isArray(update?.rollback_targets) ? update.rollback_targets : [];
  const trustUpdate = gateway.trust_update || null;
  const online = gateway.status === "online";
  const versionLabel = runtimeVersionLabel(update);
  // Durable enrollment intent is authoritative. A missing field is an invalid
  // legacy response, not permission to infer FR202 from stale heartbeat data.
  const hardwareProfile = edgeHardwareProfileFromCatalog(gateway.hardware_profile, hardwareProfiles);
  const ioPresentation = runtimeIOPresentation(hardwareProfile);
  const cloudTransport = gateway.cloud_transport === "mqtt" ? "MQTT" : "WebSocket";
  return (
    <div className="plcRuntimeDetailPage gwFormStack" data-gw-resource="edge_gateways" data-gw-id={gateway.id} data-gw-name={gateway.name}>
      <div>
        <button type="button" className="gwGhostBtn plcRuntimeDetailBack" onClick={onBack}>
          <img src={chevronIcon} alt="" className="gwIcon" />
          All runtimes
        </button>
      </div>
      <div className="gwPageHeader">
        <div>
          <div className="gwPageTitle plcRuntimeDetailTitleRow">
            {gateway.name}
            <GatewayStatusPill tone={online ? "isPositive" : gateway.last_seen_at ? "isWarning" : "isNeutral"}>
              {online ? "Online" : gateway.last_seen_at ? "Offline" : "Waiting for first connection"}
            </GatewayStatusPill>
            {updateAvailable && (
              <GatewayStatusPill tone="isWarning" title={updateState.reason}>
                Update available
              </GatewayStatusPill>
            )}
            {updateInProgress && (
              <GatewayStatusPill tone="isWarning" title={updateState.reason}>
                Updating…
              </GatewayStatusPill>
            )}
            {manualUpdate && (
              <GatewayStatusPill tone="isNeutral" title={updateState.reason}>
                Update by re-running setup
              </GatewayStatusPill>
            )}
            {updatePaused && (
              <GatewayStatusPill tone="isWarning" title={updateState.reason}>
                Update paused
              </GatewayStatusPill>
            )}
          </div>
          <div className="gwPageSubtitle">
            {versionLabel ? `Runtime ${versionLabel} · ` : ""}Last seen {formatLastSeen(gateway.last_seen_at) || "never"}
          </div>
        </div>
        <button type="button" className="gwGhostBtn" onClick={() => openRuntimeAI(gateway, "Diagnose current PLC application and I/O status")}>Ask AI about this runtime</button>
      </div>

      {gateway.enrollment_settings_review_required === true ? (
        <div className="gwNotice isWarning plcRuntimeAdvancedNotice">
          <div>
            <strong>Setup review required</strong>
            <span>Confirm this runtime's hardware and synchronization settings.</span>
          </div>
          <button type="button" className="gwPrimaryBtn" disabled={!canMaintainRuntime} onClick={onReviewSettings}>Review setup</button>
        </div>
      ) : null}

      <div className="gwCardSection">
        <div className="gwSectionTitle">Overview</div>
        <div className="gwCardSectionBody">
          <div className="plcRuntimeDetailSummary">
            <div><span>Status</span><strong>{online ? "Online" : gateway.last_seen_at ? "Offline" : "Not connected"}</strong></div>
            <div><span>Applications</span><strong>{counts.applications ?? "—"}</strong></div>
            <div><span>Connections</span><strong>{counts.connections ?? "—"}</strong></div>
            <div><span>Last seen</span><strong>{formatLastSeen(gateway.last_seen_at) || "Never"}</strong></div>
            <div><span>Hardware</span><strong>{hardwareProfile?.label || "Not reported"}</strong></div>
            <div><span>Cloud transport</span><strong>{cloudTransport}</strong></div>
          </div>
        </div>
      </div>

      <div className="gwCardSection" data-gw-card="edge_device_performance">
        <div className="gwSectionTitle">Device performance</div>
        <div className="gwCardSectionBody">
          {devicePerformance ? (
            <>
              <div className="gwStatsGrid">
                <div className="gwStatCard" data-gw-stat="edge-cpu">
                  <div className="gwStatLabel">CPU usage</div>
                  <div className="gwStatValue">{devicePerformance.cpuUsage == null ? "Unavailable" : formatRuntimeMetricPercent(devicePerformance.cpuUsage)}</div>
                  <div className="gwStatHint">{devicePerformance.cpuUsage == null ? "No valid reading reported" : devicePerformance.cpuUsage < 70 ? "Normal load" : devicePerformance.cpuUsage < 90 ? "Elevated load" : "High load"}</div>
                </div>
                {devicePerformance.cpuTemperature == null ? null : (
                  <div className="gwStatCard" data-gw-stat="edge-cpu-temperature">
                    <div className="gwStatLabel">Processor temperature</div>
                    <div className="gwStatValue">{formatRuntimeTemperature(devicePerformance.cpuTemperature)}</div>
                    <div className="gwStatHint">Reported by this device</div>
                  </div>
                )}
                <div className="gwStatCard" data-gw-stat="edge-memory">
                  <div className="gwStatLabel">Memory</div>
                  <div className="gwStatValue">{devicePerformance.memoryUsage == null ? "Unavailable" : formatRuntimeMetricBytes(devicePerformance.memoryUsage)}</div>
                  <div className="gwStatHint">{devicePerformance.memoryTotal == null ? "No valid reading reported" : `${formatRuntimeMetricBytes(devicePerformance.memoryTotal)} total · ${formatRuntimeMetricPercent(devicePerformance.memoryUtilization)}`}</div>
                </div>
                <div className="gwStatCard" data-gw-stat="edge-disk">
                  <div className="gwStatLabel">Disk</div>
                  <div className="gwStatValue">{devicePerformance.diskUtilization == null ? "Unavailable" : formatRuntimeMetricPercent(devicePerformance.diskUtilization)}</div>
                  <div className="gwStatHint">{devicePerformance.diskAvailable == null ? "No valid reading reported" : `${formatRuntimeMetricBytes(devicePerformance.diskAvailable)} free`}</div>
                </div>
                <div className="gwStatCard" data-gw-stat="edge-uptime">
                  <div className="gwStatLabel">Control Seat uptime</div>
                  <div className="gwStatValue">{devicePerformance.uptimeSeconds == null ? "Unavailable" : formatRuntimeUptime(devicePerformance.uptimeSeconds)}</div>
                  <div className="gwStatHint">{devicePerformance.uptimeSeconds == null ? "No valid reading reported" : online ? "Reported by this device" : "Last reported before disconnect"}</div>
                </div>
              </div>
              <div className="gwHint">Host resource health is reported by the device.</div>
            </>
          ) : (
            <div className="gwHint">Performance data is not available from this runtime yet.</div>
          )}
          {plcPerformance ? (
            <>
              <div className="gwSectionTitle">PLC execution</div>
              <div className="gwStatsGrid" data-gw-card="edge_plc_performance">
                <div className="gwStatCard" data-gw-stat="edge-plc-applications">
                  <div className="gwStatLabel">Applications running</div>
                  <div className="gwStatValue">{plcPerformance.running} of {plcPerformance.applications}</div>
                  <div className="gwStatHint">Latest device snapshot</div>
                </div>
                <div className="gwStatCard" data-gw-stat="edge-plc-average-scan">
                  <div className="gwStatLabel">Average scan</div>
                  <div className="gwStatValue">{formatRuntimeScanDuration(plcPerformance.averageScanUs)}</div>
                  <div className="gwStatHint">Weighted across reported scans</div>
                </div>
                <div className="gwStatCard" data-gw-stat="edge-plc-longest-scan">
                  <div className="gwStatLabel">Longest scan</div>
                  <div className="gwStatValue">{formatRuntimeScanDuration(plcPerformance.maxScanUs)}</div>
                  <div className="gwStatHint">Highest application maximum</div>
                </div>
                <div className="gwStatCard" data-gw-stat="edge-plc-overruns">
                  <div className="gwStatLabel">Cycle overruns</div>
                  <div className="gwStatValue">{plcPerformance.overruns.toLocaleString()}</div>
                  <div className="gwStatHint">Across {plcPerformance.scans.toLocaleString()} scans</div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className="gwCardSection">
        <div className="gwSectionTitleRow gwSectionTitle">
          <span>Applications</span>
          <span className="gwSectionBadge">{counts.applications ?? "—"}</span>
        </div>
        <div className="gwCardSectionBody">
          <div className="gwHint">Review deployed applications and their latest reported run state.</div>
          <div className="gwRowActions" style={{ justifyContent: "flex-start" }}>
            <button type="button" className="gwPrimaryBtn" onClick={onApplications}>Manage applications</button>
            <button type="button" className="gwGhostBtn" onClick={() => openRuntimeAI(gateway, "Help me deploy or run a PLC application safely")}>Ask AI for deployment help</button>
          </div>
        </div>
      </div>

      <div className="gwCardSection">
        <div className="gwSectionTitleRow gwSectionTitle">
          <span>I/O & connections</span>
          <span className="gwSectionBadge">{counts.connections ?? "—"}</span>
        </div>
        <div className="gwCardSectionBody">
          <div className="gwHint">{ioPresentation.description}</div>
          {availableFieldbusProtocols.length > 0 ? (
            <div className="gwEdgeProgramSection">
              <div className="gwSectionTitle">Available industrial protocols</div>
              {availableFieldbusProtocols.map((protocol) => (
                <div className="gwEdgeFieldbusRow" key={protocol.protocol}>
                  <div className="gwListTitle">{fieldbusProtocolLabel(protocol.protocol)}</div>
                  <GatewayStatusPill tone="isPositive">Ready</GatewayStatusPill>
                </div>
              ))}
            </div>
          ) : null}
          <div className="gwRowActions" style={{ justifyContent: "flex-start" }}>
            <button type="button" className="gwPrimaryBtn" onClick={onHardwareSetup}>Hardware &amp; I/O</button>
            <button type="button" className="gwGhostBtn" onClick={() => onConnections("")}>Manage connections</button>
            <button type="button" className="gwGhostBtn" onClick={() => openRuntimeAI(gateway, ioPresentation.aiTask)}>Ask AI to connect I/O</button>
          </div>
        </div>
      </div>

      <div className="gwCardSection">
        <div className="gwSectionTitle">Maintenance</div>
        <div className="gwCardSectionBody gwFormStack">
          <div className="gwEdgeFieldbusRow" data-gw-runtime-security>
            <div>
              <div className="gwListTitle">{deploymentSecurity.label}</div>
              <div className="gwListMeta">{deploymentSecurity.title}</div>
              {deploymentSecurity.error ? <div className="plcDeploymentSecurityNoticeError">{deploymentSecurity.error}</div> : null}
            </div>
            <GatewayStatusPill tone={deploymentSecurity.pillClass || "isNeutral"}>{deploymentSecurity.status}</GatewayStatusPill>
          </div>
          <FR202RetirementMaintenance
            gateway={gateway}
            canConfigure={canConfigure}
            canWrite={canWrite}
            onChanged={onChanged}
          />
          {manualUpdate ? (
            <div className="gwHint" role="status">
              A newer runtime is available, but this device does not self-update.
              Use <strong>Reconnect runtime</strong> below to get a fresh setup
              command, then run it on the device to install the update.
            </div>
          ) : null}
          {updateInProgress ? (
            <div className="gwNotice isSoft" role="status" aria-live="polite">
              <div>
                <strong>Runtime update in progress</strong>
                <span>The device may briefly appear offline while it installs and restarts. This page refreshes automatically.</span>
              </div>
            </div>
          ) : null}
          <div className="plcRuntimeAdvancedActions">
            {updateAvailable || updateInProgress ? (
              <button
                type="button"
                className="gwGhostBtn"
                data-gw-action="update-runtime"
                aria-busy={busyID === `update:${gateway.id}` || updateInProgress}
                disabled={!canMaintainRuntime || busyID === `update:${gateway.id}` || updateInProgress}
                onClick={onUpdateRuntime}
              >
                {busyID === `update:${gateway.id}` ? "Starting update…" : updateInProgress ? "Updating…" : "Update runtime"}
              </button>
            ) : null}
            {rollbackTargets.length > 0 && onRollbackRuntime ? (
              <button
                type="button"
                className="gwGhostBtn"
                data-gw-action="rollback-runtime"
                disabled={!canMaintainRuntime || !!update?.rollback_error || busyID === `rollback:${gateway.id}`}
                title={update?.rollback_error ? "Rollback is temporarily unavailable." : "Go back to an earlier runtime version kept on this device"}
                onClick={onRollbackRuntime}
              >
                Roll back runtime
              </button>
            ) : null}
            {update?.rollback_error && rollbackTargets.length === 0 ? <span className="gwHint">Rollback is temporarily unavailable.</span> : null}
            {trustUpdate?.update_available && onUpdateTrust ? (
              <button
                type="button"
                className="gwGhostBtn"
                data-gw-action="update-runtime-trust"
                disabled={!canMaintainRuntime || !!trustUpdate.error || busyID === `trust:${gateway.id}`}
                title={trustUpdate.error ? "The security update is temporarily unavailable." : "Review and apply the next device security update"}
                onClick={onUpdateTrust}
              >
              Update device security
              </button>
            ) : null}
            {trustUpdate?.supported && trustUpdate?.error ? <span className="gwHint">The security update is temporarily unavailable.</span> : null}
            {gateway.enrollment_settings_review_required !== true ? (
              <button type="button" className="gwGhostBtn" disabled={!canMaintainRuntime} onClick={onReviewSettings}>Review setup settings</button>
            ) : null}
            <button type="button" className="gwGhostBtn" disabled={!canMaintainRuntime || busyID === `rotate:${gateway.id}`} onClick={onRotate}>Reconnect runtime</button>
            <button type="button" className="gwDangerBtn" disabled={!canMaintainRuntime || busyID === `delete:${gateway.id}`} onClick={onRemove}>Remove runtime</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RuntimeRow({ gateway, hardwareProfiles, onOpen, onReview }) {
  const online = gateway.status === "online";
  const neverSeen = !gateway.last_seen_at;
  const lastSeen = formatLastSeen(gateway.last_seen_at);
  const pillClass = online ? "isPositive" : neverSeen ? "isNeutral" : "isWarning";
  const pillLabel = online ? "Online" : neverSeen ? "Never connected" : "Offline";
  const update = gateway.runtime_update || null;
  const updateState = runtimeUpdateState(gateway);
  const updateInProgress = updateState.mode === "updating";
  const updateAvailable = updateState.mode === "self";
  const manualUpdate = updateState.mode === "manual";
  const updatePaused = updateState.mode === "paused";
  const versionLabel = runtimeVersionLabel(update);
  const deploymentSecurity = plcDeploymentSecurityView(gateway);
  const counts = runtimeInventoryCounts(gateway);
  const hardwareProfile = edgeHardwareProfileFromCatalog(gateway.hardware_profile, hardwareProfiles);
  const cloudTransport = gateway.cloud_transport === "mqtt" ? "MQTT" : "WebSocket";
  return (
    <div className="gwEdgeRuntimeRow" data-gw-resource="edge_gateways" data-gw-id={gateway.id} data-gw-name={gateway.name}>
      <div className="gwEdgeRuntimeMain">
        <div className="gwEdgeRuntimeTitleRow">
          <span className="gwListTitle">{gateway.name}</span>
          <GatewayStatusPill tone={pillClass}>{pillLabel}</GatewayStatusPill>
          {updateAvailable && (
            <GatewayStatusPill tone="isWarning" title={updateState.reason}>
              Update available
            </GatewayStatusPill>
          )}
          {updateInProgress && (
            <GatewayStatusPill tone="isWarning" title={updateState.reason}>
              Updating…
            </GatewayStatusPill>
          )}
          {manualUpdate && (
            <GatewayStatusPill tone="isNeutral" title={updateState.reason}>
              Update by re-running setup
            </GatewayStatusPill>
          )}
          {updatePaused && (
            <GatewayStatusPill tone="isWarning" title={updateState.reason}>
              Update paused
            </GatewayStatusPill>
          )}
          {/* Signing status only earns a pill when something needs attention;
              healthy/not-yet-reported rows stay quiet to keep the row scannable. */}
          {deploymentSecurity.pillClass === "isWarning" && (
            <GatewayStatusPill tone={deploymentSecurity.pillClass} title={deploymentSecurity.title}>
              {deploymentSecurity.status === "Off" ? "PLC security off" : "PLC security needs attention"}
            </GatewayStatusPill>
          )}
          {deploymentSecurity.error && (
            <GatewayStatusPill tone="isWarning" title={deploymentSecurity.error}>
              PLC deploy rejected
            </GatewayStatusPill>
          )}
          {gateway.enrollment_settings_review_required === true ? (
            <GatewayStatusPill tone="isWarning" title="Confirm the runtime hardware and synchronization settings">
              Setup review required
            </GatewayStatusPill>
          ) : null}
        </div>
        <div className="gwListMeta">
          {versionLabel ? `Runtime ${versionLabel}` : "Runtime version not reported"}
          {lastSeen ? ` · Last seen ${lastSeen}` : " · Has never connected"}
        </div>
        {hardwareProfile ? <div className="gwListMeta">Hardware {hardwareProfile.label} · Cloud transport {cloudTransport}</div> : null}
        <div className="gwListMeta">Applications {counts.applications ?? "—"} · Connections {counts.connections ?? "—"}</div>
      </div>
      <div className="gwRowActions">
        {gateway.enrollment_settings_review_required === true && onReview ? (
          <button type="button" className="gwRowAction" data-gw-action="review-runtime-setup" onClick={onReview}>Review setup</button>
        ) : null}
        <button type="button" className="gwRowAction" data-gw-action="open-runtime" onClick={onOpen}>Open</button>
      </div>
    </div>
  );
}
